import type { Connection } from '@xyflow/react'

import type { HandleDataType, StudioFlowEdge, StudioFlowNode, StudioNodeType } from './types'
import { HANDLE_COLORS } from './types'

/** Handle id 约定：`${role}-${dataType}`，如 `in-text` / `out-image` */
export interface HandleSpec {
  id: string
  dataType: HandleDataType
  /** 多连接 handle（如 ref_character 的 image 输出可接多个下游） */
  multiple?: boolean
}

export interface NodeHandleSpec {
  inputs: HandleSpec[]
  outputs: HandleSpec[]
}

/** 节点的输入/输出 Handle 规格 */
export const NODE_HANDLES: Record<StudioNodeType, NodeHandleSpec> = {
  context: { inputs: [], outputs: [{ id: 'out-ctx', dataType: 'ctx' }] },
  text: { inputs: [{ id: 'in-text', dataType: 'text' }], outputs: [{ id: 'out-text', dataType: 'text' }] },
  prompt: { inputs: [], outputs: [{ id: 'out-text', dataType: 'text' }] },
  upload: { inputs: [], outputs: [{ id: 'out-image', dataType: 'image' }, { id: 'out-video', dataType: 'video' }, { id: 'out-audio', dataType: 'audio' }] },
  ref_character: { inputs: [], outputs: [{ id: 'out-image', dataType: 'image', multiple: true }] },
  ref_scene: { inputs: [], outputs: [{ id: 'out-image', dataType: 'image', multiple: true }] },

  gen_image: {
    inputs: [
      { id: 'in-text', dataType: 'text' },
      { id: 'in-image', dataType: 'image', multiple: true },
    ],
    outputs: [{ id: 'out-image', dataType: 'image' }],
  },
  gen_video: {
    inputs: [
      { id: 'in-text', dataType: 'text' },
      { id: 'in-image', dataType: 'image', multiple: true },
    ],
    outputs: [{ id: 'out-video', dataType: 'video' }],
  },
  gen_audio: {
    inputs: [
      { id: 'in-text', dataType: 'text' },
      { id: 'in-audio', dataType: 'audio', multiple: true },
    ],
    outputs: [{ id: 'out-audio', dataType: 'audio' }],
  },

  register: {
    inputs: [
      { id: 'in-image', dataType: 'image' },
      { id: 'in-video', dataType: 'video' },
      { id: 'in-audio', dataType: 'audio' },
    ],
    outputs: [],
  },
  export: {
    inputs: [
      { id: 'in-video', dataType: 'video' },
      { id: 'in-audio', dataType: 'audio' },
    ],
    outputs: [{ id: 'out-video', dataType: 'video' }],
  },

  group: { inputs: [], outputs: [] },
}

/** 上游输出类型能否接入目标输入类型（ctx 可喂 text Prompt） */
export function areHandleDataTypesCompatible(
  sourceType: HandleDataType,
  targetType: HandleDataType,
): boolean {
  if (sourceType === targetType) return true
  if (sourceType === 'ctx' && targetType === 'text') return true
  return false
}

function connectionExists(
  edges: StudioFlowEdge[],
  source: string,
  target: string,
  sourceHandle: string | null,
  targetHandle: string | null,
): boolean {
  return edges.some(
    (e) =>
      e.source === source &&
      e.target === target &&
      (e.sourceHandle ?? null) === sourceHandle &&
      (e.targetHandle ?? null) === targetHandle,
  )
}

function targetHandleTaken(
  edges: StudioFlowEdge[],
  targetId: string,
  targetHandle: string,
  multiple?: boolean,
): boolean {
  if (multiple) return false
  return edges.some((e) => e.target === targetId && e.targetHandle === targetHandle)
}

/** 两端 handle 数据类型是否兼容（不自动改口） */
export function isValidConnectionTypes(connection: Connection): boolean {
  if (!connection.source || !connection.target) return false
  if (connection.source === connection.target) return false
  const srcType = parseHandleDataType(connection.sourceHandle)
  const tgtType = parseHandleDataType(connection.targetHandle)
  if (!srcType || !tgtType) return false
  return areHandleDataTypesCompatible(srcType, tgtType)
}

/** React Flow 给出的端点是否可连（类型 + 占用，不自动配口） */
export function isValidStudioConnection(
  connection: Connection,
  edges: StudioFlowEdge[],
  nodes: StudioFlowNode[],
): boolean {
  if (!isValidConnectionTypes(connection)) return false
  const source = nodes.find((n) => n.id === connection.source)
  const target = nodes.find((n) => n.id === connection.target)
  if (!source || !target) return false
  if (source.data.nodeType === 'group' || target.data.nodeType === 'group') return false
  if (
    connectionExists(
      edges,
      connection.source,
      connection.target,
      connection.sourceHandle ?? null,
      connection.targetHandle ?? null,
    )
  ) {
    return false
  }
  if (!connection.targetHandle) return false
  const targetSpec = handlesOf(target.data.nodeType, 'in').find((h) => h.id === connection.targetHandle)
  if (!targetSpec) return false
  if (targetHandleTaken(edges, connection.target, connection.targetHandle, targetSpec.multiple)) {
    return false
  }
  return true
}

/** upload 节点：仅暴露与已上传素材匹配的输出 handle，避免拖线误选 video/audio 口 */
export function uploadOutputHandles(node: {
  data: { upload?: { mime?: string; url?: string }; output?: { dataType?: HandleDataType } }
}): HandleSpec[] {
  const all = NODE_HANDLES.upload.outputs
  const mime = node.data.upload?.mime ?? ''
  const dt = node.data.output?.dataType
  if (mime.startsWith('video/') || dt === 'video') return all.filter((h) => h.dataType === 'video')
  if (mime.startsWith('audio/') || dt === 'audio') return all.filter((h) => h.dataType === 'audio')
  if (node.data.upload?.url || mime.startsWith('image/') || dt === 'image') {
    return all.filter((h) => h.dataType === 'image')
  }
  return all
}

/** 从 handle id 解析数据类型 */
export function parseHandleDataType(handleId: string | null | undefined): HandleDataType | null {
  if (!handleId) return null
  const parts = handleId.split('-')
  if (parts.length < 2) return null
  const dt = parts.slice(1).join('-') as HandleDataType
  return dt in HANDLE_COLORS ? dt : null
}

/** 该节点是否接受某数据类型的输入 handle */
export function nodeAcceptsInput(nodeType: StudioNodeType, dataType: HandleDataType): boolean {
  return NODE_HANDLES[nodeType].inputs.some((h) => areHandleDataTypesCompatible(dataType, h.dataType))
}

/** 该节点是否提供某数据类型的输出 handle */
export function nodeProducesOutput(nodeType: StudioNodeType, dataType: HandleDataType): boolean {
  return NODE_HANDLES[nodeType].outputs.some((h) => areHandleDataTypesCompatible(h.dataType, dataType))
}

/** 取节点某方向的 handle 列表 */
export function handlesOf(nodeType: StudioNodeType, role: 'in' | 'out'): HandleSpec[] {
  return role === 'in' ? NODE_HANDLES[nodeType].inputs : NODE_HANDLES[nodeType].outputs
}

export function handleColor(dataType: HandleDataType): string {
  return HANDLE_COLORS[dataType]
}

/** 同侧多端点：以边线垂直正中为基准，上下对称错开（px） */
export const HANDLE_EDGE_STEP_PX = 26

export function handleEdgeOffsetY(index: number, total: number): number {
  if (total <= 1) return 0
  return Math.round((index - (total - 1) / 2) * HANDLE_EDGE_STEP_PX)
}

/** 连接端点悬停提示文案 key */
export function handleTooltipKey(spec: HandleSpec, role: 'in' | 'out'): string {
  const prefix = role === 'in' ? 'in' : 'out'
  const suffix = spec.multiple ? 'Multiple' : ''
  return `canvas.handle.${prefix}.${spec.dataType}${suffix}`
}
